import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCommoditySettings } from '../../../../hooks/useCommoditySettings';
import { MeasurementSystem } from '../../../../providers/User/User.model';
import { CommodityList } from '../../../../redux/api/commodities/commodities.model';
import { formatDimensions, unitOptions } from '../../../../utils/commodity/commodityUtils';
import NumberUtils from '../../../../utils/numberUtils';

interface TwMobileCommodityOverlayProps {
  commodity: CommodityList;
}

// Style constants
const WIDTH_LABEL = '30%';
const WIDTH_INPUT = '75%';
const WIDTH_QTY = '5%';
const WIDTH_WEIGHT = '30%';
const COLOR_UNIT = '#9CA3AF';

// Reusable styled components
const StyledRowBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8 // gap={1} in MUI equals 8px
});
const StyledLabelBox = styled(Box)(() => ({
  width: WIDTH_LABEL
}));
const StyledInputBox = styled(Box)(() => ({
  width: WIDTH_INPUT
}));
const StyledQtyBox = styled(Box)(() => ({
  width: WIDTH_QTY
}));
const StyledWeightBox = styled(Box)(() => ({
  width: WIDTH_WEIGHT
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem'
  }
}));

const InfoTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'fontWeight'
})<{
  fontWeight?: number;
}>(({ theme, fontWeight }) => ({
  fontWeight: fontWeight ?? 400,
  color: '#374151',
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem'
  }
}));

const TwMobileCommodityOverlay: React.FC<TwMobileCommodityOverlayProps> = ({
  commodity,
}) => {
  const { 
    isDimensionsEnabled, 
    isUnitEnabled, 
    isPermitRequirementEnabled, 
    measurementSystem,
    isMakeEnabled,
    isModelEnabled,
    isCommodityIdEnabled
  } = useCommoditySettings();

  const dimensions = formatDimensions(
    commodity.length,
    commodity.width,
    commodity.height,
    measurementSystem
  );
  const unitLabel = unitOptions.find((opt) => opt.value === commodity.unit)?.label || '-';
  const permitLabel = commodity.permit ? 'Required' : 'Not required';

  return (
    <Box p={2} mb={2} borderRadius={2} boxShadow={1} bgcolor="background.paper">
      {/* Commodity type selector */}
      <Box mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1}>
            <HeaderTypography variant="h6">
              {commodity.name || '-'}{' '}
              {isCommodityIdEnabled &&
                commodity.readable_commodity_id &&
                `(ID ${commodity.readable_commodity_id})`}
            </HeaderTypography>
          </Box>
        </Box>
      </Box>

      {/* Dimensions */}
      {isDimensionsEnabled && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <StyledRowBox mb={2}>
              <StyledLabelBox>
                <Typography variant="body2">L x W x H</Typography>
              </StyledLabelBox>
              <StyledInputBox>
                <InfoTypography>{dimensions || '-'}</InfoTypography>
              </StyledInputBox>
            </StyledRowBox>
          </Grid>
        </Grid>
      )}

      {/* Make and Model */}
      {(isMakeEnabled || isModelEnabled) && (
        <Grid container spacing={2} mb={2}>
          {isMakeEnabled && (
            <Grid item xs={12}>
              <StyledRowBox>
                <StyledLabelBox>
                  <Typography variant="body2">Make</Typography>
                </StyledLabelBox>
                <StyledInputBox>
                  <Typography variant="body2">{commodity.make || '-'}</Typography>
                </StyledInputBox>
              </StyledRowBox>
            </Grid>
          )}
          {isModelEnabled && (
            <Grid item xs={12}>
              <StyledRowBox>
                <StyledLabelBox>
                <Typography variant="body2">Model</Typography>
              </StyledLabelBox>
              <StyledInputBox>
                <Typography variant="body2">{commodity.model || '-'}</Typography>
                </StyledInputBox>
              </StyledRowBox>
            </Grid>
          )}
        </Grid>
      )}

      {/* Note */}
      <StyledRowBox mb={2}>
        <StyledLabelBox>
          <Typography variant="body2">Note</Typography>
        </StyledLabelBox>
        <StyledInputBox>
          <InfoTypography>{commodity.notes || '-'}</InfoTypography>
        </StyledInputBox>
      </StyledRowBox>

      {/* Permit */}
      {isPermitRequirementEnabled && (
        <StyledRowBox mb={2}>
          <StyledLabelBox>
            <Typography variant="body2">Permit</Typography>
          </StyledLabelBox>
          <StyledInputBox>
            <InfoTypography>{permitLabel}</InfoTypography>
          </StyledInputBox>
        </StyledRowBox>
      )}

      {/* Unit */}
      {isUnitEnabled && (
        <StyledRowBox mb={2}>
          <StyledLabelBox>
            <Typography variant="body2">Unit</Typography>
          </StyledLabelBox>
          <StyledInputBox>
            <InfoTypography>{unitLabel}</InfoTypography>
          </StyledInputBox>
        </StyledRowBox>
      )}

      {/* Qty x Weight */}
      <StyledRowBox mb={2}>
        <StyledLabelBox>
          <Typography variant="body2">Qty x Weight</Typography>
        </StyledLabelBox>
        <StyledQtyBox>
          <InfoTypography fontWeight={500} sx={{ textAlign: 'left' }}>
            {commodity.quantity || '-'}
          </InfoTypography>
        </StyledQtyBox>
        <StyledQtyBox sx={{ textAlign: 'left' }}>
          <Typography variant="body2">x</Typography>
        </StyledQtyBox>
        <StyledWeightBox>
          <InfoTypography>
            {commodity.weight
              ? measurementSystem === MeasurementSystem.IMPERIAL
                ? `${NumberUtils.formatNumber(commodity.weight)} lb`
                : `${NumberUtils.formatNumber(commodity.weight)} kg`
              : '-'}
          </InfoTypography>
        </StyledWeightBox>
      </StyledRowBox>
    </Box>
  );
};

export default TwMobileCommodityOverlay;
